import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { CButton, CSpinner, CCard, CFormSwitch } from '@coreui/react';
import { getIcon, isValidCoordinate } from './utils';
import { getCategoryConfig } from '../CategoryConfig';

const MapRefSetter = ({ mapRef, onBoundsChange }) => {
    const map = useMap();
    useEffect(() => {
        if (map) {
            mapRef.current = map;
            onBoundsChange(map.getBounds());

            const onMove = () => {
                onBoundsChange(map.getBounds());
            };

            map.on('moveend', onMove);
            return () => map.off('moveend', onMove);
        }
    }, [map, mapRef, onBoundsChange]);
    return null;
};

// Componente para manejar el auto-switch de Heatmap a Pins basado en zoom
const ZoomHandler = ({ isHeatmap, setIsHeatmap, hasManualOverride }) => {
    useMapEvents({
        zoomend: (e) => {
            if (hasManualOverride) return;
            const zoom = e.target.getZoom();
            if (zoom > 16 && isHeatmap) {
                setIsHeatmap(false);
            }
            else if (zoom <= 16 && !isHeatmap) {
                setIsHeatmap(true);
            }
        }
    });
    return null;
};

// Componente para el Mapa de Calor (Heatmap) con Lazy Loading
const HeatmapLayer = ({ points }) => {
    const map = useMap();
    const heatLayerRef = useRef(null);
    const [scriptLoaded, setScriptLoaded] = useState(!!(window.L && window.L.heatLayer));

    useEffect(() => {
        if (window.L && window.L.heatLayer) {
            setScriptLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = "https://unpkg.com/leaflet.heat@0.2.0/dist/leaflet-heat.js";
        script.async = true;
        script.onload = () => setScriptLoaded(true);
        document.body.appendChild(script);
    }, []);

    useEffect(() => {
        if (!map || !points || points.length === 0 || !scriptLoaded) return;

        if (heatLayerRef.current) {
            map.removeLayer(heatLayerRef.current);
        }

        if (window.L && window.L.heatLayer) {
            const heatPoints = points
                .map(p => [parseFloat(p.latitud), parseFloat(p.longitud), 0.5])
                .filter(p => !isNaN(p[0]) && !isNaN(p[1]));

            heatLayerRef.current = window.L.heatLayer(heatPoints, {
                radius: 20,
                blur: 15,
                maxZoom: 17,
                gradient: { 0.4: 'blue', 0.6: 'cyan', 0.7: 'lime', 0.8: 'yellow', 1: 'red' }
            }).addTo(map);
        }

        return () => {
            if (heatLayerRef.current && map) {
                map.removeLayer(heatLayerRef.current);
            }
        };
    }, [map, points, scriptLoaded]);

    return null;
};


const MarkerWithPopup = ({ markerData, focusMarkerId, handleMarkerClickWithCluster, handleOpenUnifiedDetail }) => {
    const markerRef = useRef(null);

    useEffect(() => {
        if (focusMarkerId === markerData.id && markerRef.current) {
            const timer = setTimeout(() => {
                if (markerRef.current) {
                    markerRef.current.openPopup();
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [focusMarkerId, markerData.id]);

    if (!isValidCoordinate(markerData.latitud, markerData.longitud)) return null;

    return (
        <Marker
            ref={markerRef}
            position={[parseFloat(markerData.latitud), parseFloat(markerData.longitud)]}
            icon={getIcon(markerData.tipo_especifico, markerData.tipo)}
            eventHandlers={{
                click: (e) => {
                    handleMarkerClickWithCluster(markerData, e);
                }
            }}
        >
            <Popup>
                <div className="p-1" style={{ minWidth: '150px' }}>
                    <div className="fw-bold text-primary mb-1 border-bottom pb-1" style={{ fontSize: '0.9rem' }}>
                        #{markerData.id}
                    </div>
                    <div className="small mb-1"><strong>Tipo:</strong> {markerData.tipo || 'N/A'}</div>
                    <div className="small mb-2"><strong>Detalle:</strong> {markerData.tipo_especifico || 'N/A'}</div>
                    <CButton
                        color="dark"
                        size="sm"
                        className="w-100 py-1 fw-bold"
                        style={{ fontSize: '0.75rem', background: '#131133' }}
                        onClick={() => handleOpenUnifiedDetail(markerData)}
                    >
                        🔎 Ver Detalles
                    </CButton>
                </div>
            </Popup>
        </Marker>
    );
};


const MapSection = ({
    isLoading,
    position,
    mapRef,
    markersData,
    mapFilter,
    activeSubFilter,
    lastViewedRowId,
    focusMarkerId,
    handleMarkerClickWithCluster,
    handleOpenUnifiedDetail,
    forceShowAll = false
}) => {
    const [bounds, setBounds] = useState(null);
    const [isHeatmap, setIsHeatmap] = useState(true); // Heatmap por defecto
    const [hasManualOverride, setHasManualOverride] = useState(false);

    const handleBoundsChange = useCallback((newBounds) => {
        setBounds(newBounds);
    }, []);

    // Lógica para auto-activar heatmap en basura/baches/señalética y desactivar en otros
    useEffect(() => {
        if (mapFilter === 'basura' || mapFilter === 'bache' || mapFilter === 'sen_horiz') {
            setIsHeatmap(true);
            setHasManualOverride(false); // Vuelve al modo automático
        } else if (mapFilter && mapFilter !== 'all') {
            setIsHeatmap(false);
            setHasManualOverride(true); // Forzamos OFF
        }
    }, [mapFilter]);

    // Extraer y filtrar señales para el mapa
    const allSenales = markersData && (markersData.senales || (markersData.result && markersData.result.results))
        ? (markersData.senales || markersData.result.results)
        : [];

    const filteredSenales = allSenales.filter((m) => {
        if (!mapFilter || mapFilter === 'all') {
            if (forceShowAll) return true;
            if (allSenales.length < 4000) return true;
            return false;
        }

        const tipo = (m.tipo || '').toString().toLowerCase();
        const tipoEsp = (m.tipo_especifico || '').toString().toLowerCase();

        // Usar configuración modular para validación de categoría
        const config = getCategoryConfig(mapFilter);
        const categoryMatch = config.regex.test(tipoEsp + tipo);

        if (!categoryMatch) return false;

        // Usar configuración modular para sub-filtros
        if (activeSubFilter) {
            const subConfig = config.subFilters.find(f => f.id === activeSubFilter);
            if (subConfig) {
                if (!subConfig.check(m)) return false;
            }
        }

        return true;
    });

    let visibleMarkers = filteredSenales;
    if (!isHeatmap && bounds) {
        visibleMarkers = filteredSenales.filter(m => bounds.contains([parseFloat(m.latitud), parseFloat(m.longitud)]));
        if (visibleMarkers.length > 3000 && !forceShowAll) {
            visibleMarkers = visibleMarkers.filter((_, i) => i % 4 === 0);
        }
    }

    return (
        <div className="grid-item map-container" style={{ position: 'relative', minHeight: '450px' }}>
            <CCard className="border-0 shadow-sm overflow-hidden" style={{ borderRadius: '16px' }}>

                {/* Control de Heatmap flotante - Botón compacto */}
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center'
                }}>
                    <button
                        onClick={() => {
                            setHasManualOverride(true);
                            setIsHeatmap(!isHeatmap);
                        }}
                        style={{
                            background: isHeatmap ? '#131133' : 'rgba(255,255,255,0.9)',
                            color: isHeatmap ? 'white' : '#475569',
                            border: '1px solid #e2e8f0',
                            borderRadius: '20px',
                            padding: '5px 14px',
                            fontSize: '0.6rem',
                            fontWeight: '800',
                            letterSpacing: '0.5px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            backdropFilter: 'blur(4px)',
                            transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            cursor: 'pointer'
                        }}
                        className="d-flex align-items-center gap-2"
                    >
                        <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: isHeatmap ? '#4ade80' : '#cbd5e1',
                            boxShadow: isHeatmap ? '0 0 8px #4ade80' : 'none'
                        }}></div>
                        HEATMAP {isHeatmap ? 'ON' : 'OFF'}
                    </button>

                    {hasManualOverride && (
                        <button
                            onClick={() => setHasManualOverride(false)}
                            style={{
                                background: 'rgba(255,255,255,0.9)',
                                border: '1px solid #e2e8f0',
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                cursor: 'pointer',
                                color: '#64748b',
                                fontSize: '0.9rem'
                            }}
                            title="Reiniciar Modo Automático"
                        >
                            ↺
                        </button>
                    )}
                </div>

                <MapContainer
                    center={position}
                    zoom={13}
                    maxZoom={18}
                    style={{ height: "450px" }}
                >
                    <MapRefSetter mapRef={mapRef} onBoundsChange={handleBoundsChange} />
                    <ZoomHandler isHeatmap={isHeatmap} setIsHeatmap={setIsHeatmap} hasManualOverride={hasManualOverride} />

                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

                    {isHeatmap ? (
                        <HeatmapLayer points={filteredSenales} />
                    ) : (
                        <MarkerClusterGroup disableClusteringAtZoom={17}>
                            {visibleMarkers.map((markerData1) => (
                                <MarkerWithPopup
                                    key={markerData1.id}
                                    markerData={markerData1}
                                    focusMarkerId={focusMarkerId}
                                    handleMarkerClickWithCluster={handleMarkerClickWithCluster}
                                    handleOpenUnifiedDetail={handleOpenUnifiedDetail}
                                />
                            ))}
                        </MarkerClusterGroup>
                    )}
                </MapContainer>
            </CCard>
        </div>
    );
};

export default React.memo(MapSection);
